"use client";

import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { Post, Tag } from "@/app/utils/interface";
import Pagination from "@/components/utilities/Pagination";
import TagFilter from "@/components/utilities/TagFilter";
import { client } from "@/sanity/lib/client";

const POSTS_PER_PAGE = 6;

interface TagWithCount extends Tag {
  postCount: number;
}

interface CategoryListProps {
  categorySlug: string;
  isNewsroomStyle?: boolean;
}

async function getPosts(categorySlug: string, page: number = 1, tag?: string) {
  const start = (page - 1) * POSTS_PER_PAGE;
  const end = start + POSTS_PER_PAGE;

  const baseFilter = `(_type == "post" || _type == "postPressRelease") && $categorySlug in categories[]->slug.current`;
  const tagFilter = tag ? ` && $tag in tags[]->slug.current` : "";
  const filter = `${baseFilter}${tagFilter}`;

  const query = `{
    "posts": *[${filter}] | order(publishedAt desc)[$start...$end] {
      _id,
      _type,
      title,
      headline,
      navtitle,
      slug,
      publishedAt,
      excerpt,
      pageDescription,
      cloudinaryImage{
        secure_url
      },
      youtubeVideo,
      "categories": categories[]-> {
        _id,
        title,
        slug
      },
      "tags": tags[]-> {
        _id,
        name,
        slug
      }
    },
    "total": count(*[${filter}])
  }`;

  const params = tag
    ? {
        categorySlug,
        tag,
        start,
        end,
      }
    : {
        categorySlug,
        start,
        end,
      };

  // Debug logging
  console.log("CategoryList getPosts - Query params:", {
    categorySlug,
    page,
    tag,
    start,
    end,
  });
  console.log("CategoryList getPosts - Filter:", filter);

  try {
    const { posts, total } = await client.fetch(
      query,
      params as Record<string, unknown>
    );

    // Debug logging
    console.log("CategoryList getPosts - Results:", {
      postsCount: posts?.length || 0,
      total,
      posts: posts?.map((p: any) => ({
        _id: p._id,
        _type: p._type,
        title: p.title,
        headline: p.headline,
        slug: p.slug?.current,
        categories: p.categories?.map((c: any) => c.slug?.current),
      })),
    });

    // If no posts found, try a diagnostic query
    if (
      (!posts || posts.length === 0) &&
      categorySlug === "dr-john-watts-media"
    ) {
      console.log(
        "No posts found for dr-john-watts-media, running diagnostic query..."
      );
      const diagnosticQuery = `*[_type == "postPressRelease"]{
        _id,
        _type,
        title,
        headline,
        slug,
        publishedAt,
        "categories": categories[]-> {
          _id,
          title,
          slug
        }
      }`;
      const allPressReleases = await client.fetch(diagnosticQuery);
      console.log("All postPressRelease posts:", allPressReleases);

      // Check for posts without categories
      const postsWithoutCategories = allPressReleases?.filter(
        (p: any) => !p.categories || p.categories.length === 0
      );
      if (postsWithoutCategories && postsWithoutCategories.length > 0) {
        console.warn(
          `⚠️ Found ${postsWithoutCategories.length} post(s) without categories assigned:`,
          postsWithoutCategories.map((p: any) => ({
            title: p.title || p.headline,
            slug: p.slug?.current,
          }))
        );
        console.warn(
          `💡 Please assign the category "Press & Media Center" to these posts in Sanity Studio.`
        );
      }
    }

    return { posts: posts || [], total: total || 0 };
  } catch (error) {
    console.error("Error fetching posts in CategoryList:", error);
    return { posts: [], total: 0 };
  }
}

async function getAllTags(categorySlug: string) {
  const query = `*[_type == "tag"] {
    _id,
    name,
    slug,
    "postCount": count(*[(_type == "post" || _type == "postPressRelease") && $categorySlug in categories[]->slug.current && references(^._id)])
  } | order(postCount desc)`;

  const tags = await client.fetch(query, { categorySlug });
  // Filter out tags with 0 posts
  return tags.filter((tag: TagWithCount) => tag.postCount > 0);
}

export default function CategoryList({
  categorySlug,
  isNewsroomStyle = false,
}: CategoryListProps) {
  const searchParams = useSearchParams();
  const [posts, setPosts] = useState<Post[]>([]);
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const currentPage = Number(searchParams.get("page")) || 1;
  const selectedTag = searchParams.get("tag") || undefined;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [{ posts, total }, tags] = await Promise.all([
          getPosts(categorySlug, currentPage, selectedTag),
          getAllTags(categorySlug),
        ]);
        setPosts(posts);
        setTotal(total);
        setTags(tags);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, currentPage, selectedTag]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="size-32 animate-spin rounded-full border-y-2 border-indigo-600"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / POSTS_PER_PAGE);

  // Separate featured posts (first 3) from regular posts
  const featuredPosts =
    currentPage === 1 && !selectedTag ? posts.slice(0, 3) : [];
  const regularPosts =
    currentPage === 1 && !selectedTag ? posts.slice(3) : posts;

  // Newsroom Style - Press Release Category Page Layout
  if (isNewsroomStyle) {
    if (posts.length === 0) {
      return (
        <div className="py-12 text-center">
          <p className="text-gray-500">No press releases found.</p>
        </div>
      );
    }

    // Separate featured post (first one) from other posts
    const featuredPost = posts[0];
    const otherPosts = posts.slice(1);

    return (
      <>
        {/* Featured Post - Full width container with large radius, 2 columns */}
        {featuredPost &&
          (() => {
            const primaryTag = featuredPost.tags?.[0];
            const formattedDate = featuredPost.publishedAt
              ? new Date(featuredPost.publishedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "";
            const postCategorySlug =
              featuredPost.categories?.[0]?.slug?.current || categorySlug;
            const postSlug = featuredPost.slug?.current || "";
            const postTitle =
              featuredPost.headline || featuredPost.title || "Untitled";
            const featuredImage =
              featuredPost.cloudinaryImage?.secure_url ||
              (featuredPost.youtubeVideo
                ? `https://img.youtube.com/vi/${featuredPost.youtubeVideo}/maxresdefault.jpg`
                : null);

            if (!postSlug) return null;

            return (
              <article className="group mb-12 rounded-3xl bg-gray-50 p-6 shadow-[0_0_15px_rgba(0,0,0,0.1)] transition-colors hover:bg-white">
                <Link
                  href={`/${postCategorySlug}/${postSlug}`}
                  className="block"
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Left Column - Image */}
                    <div className="relative h-[500px] w-full shrink-0 overflow-hidden rounded-2xl lg:w-[500px]">
                      {featuredImage ? (
                        <Image
                          src={featuredImage}
                          alt={postTitle}
                          fill
                          className="object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                          sizes="500px"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                          <span className="text-gray-400">No Image</span>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Tag, Title, Date */}
                    <div className="flex flex-col justify-center px-6 lg:w-1/2">
                      {primaryTag && (
                        <div className="mb-3">
                          <span className="inline-block rounded-full border border-gray-300 px-4 py-1.5 text-sm font-semibold uppercase tracking-wide text-gray-600">
                            {primaryTag.name}
                          </span>
                        </div>
                      )}
                      <h2 className="mb-4 text-2xl font-bold leading-tight text-gray-900 transition-colors hover:text-gray-700 sm:text-3xl lg:text-4xl">
                        {postTitle}
                      </h2>
                      {formattedDate && (
                        <div className="text-sm text-gray-500">
                          {formattedDate}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </article>
            );
          })()}

        {/* Other Posts - 2 columns grid */}
        {otherPosts.length > 0 && (
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {otherPosts.map((post: Post) => {
              if (!post || !post._id) return null;

              const primaryTag = post.tags?.[0];
              const formattedDate = post.publishedAt
                ? new Date(post.publishedAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "";
              const postCategorySlug =
                post.categories?.[0]?.slug?.current || categorySlug;
              const postSlug = post.slug?.current || "";
              const postTitle = post.headline || post.title || "Untitled";
              const postImage =
                post.cloudinaryImage?.secure_url ||
                (post.youtubeVideo
                  ? `https://img.youtube.com/vi/${post.youtubeVideo}/maxresdefault.jpg`
                  : null);

              if (!postSlug) return null;

              return (
                <article
                  key={post._id}
                  className="group rounded-3xl bg-gray-50 p-6 shadow-[0_0_15px_rgba(0,0,0,0.1)] transition-all hover:bg-white hover:shadow-[0_0_20px_rgba(0,0,0,0.15)]"
                >
                  <Link
                    href={`/${postCategorySlug}/${postSlug}`}
                    className="block"
                  >
                    {/* Image */}
                    <div className="relative mb-4 h-56 w-full overflow-hidden rounded-2xl sm:h-64">
                      {postImage ? (
                        <Image
                          src={postImage}
                          alt={postTitle}
                          fill
                          className="object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                          sizes="(max-width: 768px) 100vw, 50vw"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                          <span className="text-gray-400">No Image</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div>
                      {primaryTag && (
                        <div className="mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            {primaryTag.name}
                          </span>
                        </div>
                      )}
                      <h3 className="mb-3 text-lg font-bold leading-tight text-gray-900 transition-colors group-hover:text-gray-700 sm:text-xl">
                        {postTitle}
                      </h3>
                      {formattedDate && (
                        <div className="text-sm text-gray-500">
                          {formattedDate}
                        </div>
                      )}
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-12 border-t border-gray-200 pt-8">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              selectedTag={selectedTag}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="mb-8">
        <TagFilter tags={tags} selectedTag={selectedTag} />
      </div>

      {/* Featured Section - Only on first page without tag filter */}
      {featuredPosts.length > 0 && (
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* Left Column - Large Featured Post */}
          <div className="flex-shrink-0 lg:max-w-[700px]">
            {featuredPosts[0] && (
              <article className="group flex flex-col">
                <Link
                  href={`/${featuredPosts[0].categories[0]?.slug.current}/${featuredPosts[0].slug.current}`}
                  className="block mb-4"
                >
                  {featuredPosts[0].cloudinaryImage?.secure_url ||
                  featuredPosts[0].youtubeVideo ? (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl">
                      <Image
                        src={
                          featuredPosts[0].cloudinaryImage?.secure_url ||
                          `https://img.youtube.com/vi/${featuredPosts[0].youtubeVideo}/maxresdefault.jpg`
                        }
                        alt={featuredPosts[0].title}
                        width={700}
                        height={390}
                        className="w-full h-full object-cover rounded-2xl transition-transform duration-700 ease-in-out group-hover:scale-105"
                        onError={(e) => {
                          if (featuredPosts[0].youtubeVideo) {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${featuredPosts[0].youtubeVideo}/hqdefault.jpg`;
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-8 transition-all duration-300 group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-pink-600">
                      <h3 className="text-3xl font-bold text-white text-center leading-tight md:text-4xl">
                        {featuredPosts[0].navtitle || featuredPosts[0].title}
                      </h3>
                    </div>
                  )}
                </Link>
                <div className="flex flex-1 flex-col">
                  <h2 className="mb-4 text-3xl font-bold leading-tight text-gray-900 lg:text-[60px]">
                    <Link
                      href={`/${featuredPosts[0].categories[0]?.slug.current}/${featuredPosts[0].slug.current}`}
                      className="transition-colors hover:text-gray-600 hover:underline"
                    >
                      {featuredPosts[0].title}
                    </Link>
                  </h2>
                  {featuredPosts[0].pageDescription && (
                    <p className="mb-4 text-lg leading-relaxed text-gray-600">
                      {featuredPosts[0].pageDescription}
                    </p>
                  )}
                  {featuredPosts[0].excerpt &&
                    !featuredPosts[0].pageDescription && (
                      <p className="mb-4 text-lg leading-relaxed text-gray-600">
                        {featuredPosts[0].excerpt}
                      </p>
                    )}
                  {featuredPosts[0].tags &&
                    featuredPosts[0].tags.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-2">
                        {featuredPosts[0].tags
                          .slice(0, 3)
                          .map(
                            (tag: {
                              _id: string;
                              name: string;
                              slug: { current: string };
                            }) => (
                              <Link
                                key={tag._id}
                                href={`?tag=${tag.slug.current}`}
                                className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-900 hover:text-white"
                              >
                                {tag.name}
                              </Link>
                            )
                          )}
                      </div>
                    )}
                </div>
              </article>
            )}
          </div>

          {/* Right Column - Two Medium Posts - Extended */}
          <div className="flex flex-1 flex-col gap-6 lg:pl-8">
            {featuredPosts.slice(1, 3).map((post: Post) => (
              <article key={post._id} className="group flex flex-col">
                <div className="flex gap-4">
                  <div className="flex flex-1 flex-col min-w-0">
                    <h3 className="mb-2 text-lg font-semibold leading-tight text-gray-900 lg:text-xl">
                      <Link
                        href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                        className="transition-colors hover:text-gray-600 hover:underline line-clamp-2"
                      >
                        {post.title}
                      </Link>
                    </h3>
                    {post.pageDescription && (
                      <p className="mb-2 line-clamp-2 text-sm leading-relaxed text-gray-600">
                        {post.pageDescription}
                      </p>
                    )}
                    {post.excerpt && !post.pageDescription && (
                      <p className="mb-2 line-clamp-2 text-sm leading-relaxed text-gray-600">
                        {post.excerpt}
                      </p>
                    )}
                    {post.tags && post.tags.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-1.5">
                        {post.tags
                          .slice(0, 2)
                          .map(
                            (tag: {
                              _id: string;
                              name: string;
                              slug: { current: string };
                            }) => (
                              <Link
                                key={tag._id}
                                href={`?tag=${tag.slug.current}`}
                                className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
                              >
                                {tag.name}
                              </Link>
                            )
                          )}
                      </div>
                    )}
                  </div>
                  <Link
                    href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                    className="relative w-24 h-24 shrink-0 overflow-hidden rounded-2xl"
                  >
                    {post.cloudinaryImage?.secure_url || post.youtubeVideo ? (
                      <Image
                        src={
                          post.cloudinaryImage?.secure_url ||
                          `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`
                        }
                        alt={post.title}
                        fill
                        sizes="96px"
                        className="rounded-2xl object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                        onError={(e) => {
                          if (post.youtubeVideo) {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`;
                          }
                        }}
                      />
                    ) : (
                      <div className="h-full w-full rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-2">
                        <span className="text-xs font-bold text-white text-center leading-tight line-clamp-3">
                          {post.navtitle || post.title}
                        </span>
                      </div>
                    )}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {/* Regular Posts Grid */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {regularPosts.map((post: Post) => (
          <article
            key={post._id}
            className="group flex flex-col transition-all duration-300"
          >
            <Link
              href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
              className="block"
            >
              {post.cloudinaryImage?.secure_url || post.youtubeVideo ? (
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-gray-100">
                  <Image
                    src={
                      post.cloudinaryImage?.secure_url ||
                      `https://img.youtube.com/vi/${post.youtubeVideo}/maxresdefault.jpg`
                    }
                    alt={post.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                    style={{ borderRadius: "1.5rem" }}
                    onError={(e) => {
                      if (post.youtubeVideo) {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center p-6 transition-all duration-300 group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-pink-600">
                  <h3 className="text-xl font-bold text-white text-center leading-tight md:text-2xl">
                    {post.navtitle || post.title}
                  </h3>
                </div>
              )}
            </Link>
            <div className="flex flex-1 flex-col pt-4">
              <h2 className="mb-3 text-2xl font-semibold leading-tight text-gray-900">
                <Link
                  href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                  className="transition-colors hover:text-gray-600 hover:underline"
                >
                  {post.title}
                </Link>
              </h2>
              {post.pageDescription && (
                <p className="mb-3 text-base leading-relaxed text-gray-600">
                  {post.pageDescription}
                </p>
              )}
              {post.excerpt && (
                <p className="line-clamp-2 text-base leading-relaxed text-gray-600">
                  {post.excerpt}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {post.tags
                  ?.slice(0, 2)
                  .map(
                    (tag: {
                      _id: string;
                      name: string;
                      slug: { current: string };
                    }) => (
                      <Link
                        key={tag._id}
                        href={`?tag=${tag.slug.current}`}
                        className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
                      >
                        {tag.name}
                      </Link>
                    )
                  )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-12">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          selectedTag={selectedTag}
        />
      </div>
    </>
  );
}
